import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SocialAuthButtons from "../components/common/SocialAuthButtons";
import { Eye, EyeOff } from "lucide-react";

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    company_name: "",
    phone: "",
    role: "Customer",
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Enforce pure light mode for signup screen
  React.useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await signup({ ...formData, rememberMe });
      if (res.success) {
        toast.success(`Account created successfully! 29-Day Free Trial activated as ${formData.role}.`);
        setTimeout(() => {
          navigate(res.homeRoute || (formData.role === "Customer" ? "/admin/customer-portal" : "/admin/dashboard"));
        }, 600);
      } else {
        toast.error(res.message || "Failed to create account. Please try again.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      toast.error("An unexpected error occurred during signup.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-auth-page="true" className="min-h-screen flex items-center justify-center bg-[#F7F4EE] px-4 py-8 auth-light-only">
      <div className="bg-white shadow-xl rounded-2xl p-8 w-full max-w-lg border border-[#E1E7EE]">
        <div className="text-center mb-6">
          <img 
            src="/assets/branding/briqerp-logo-light.svg" 
            alt="BriqERP" 
            className="h-10 sm:h-12 w-auto mx-auto object-contain mb-2"
          />
          <h2 className="text-xl font-bold text-[#1B2A41]">
            Create BriqERP Account
          </h2>
          <p className="text-xs text-[#6B7A90] font-medium mt-1">
            Sign up and get instant access with a 29-day complimentary free trial
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          <div>
            <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Rajesh Mehta"
              className="w-full border border-gray-300 px-3.5 py-2.5 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
              Work / Business Email *
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="name@company.com"
              className="w-full border border-gray-300 px-3.5 py-2.5 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                required
                minLength={6}
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 6 characters"
                className="w-full border border-gray-300 px-3.5 py-2.5 pr-10 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Company and Phone are omitted for Customer registrations */}
          {formData.role !== "Customer" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
                  Company / Organization
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Acme Infra Ltd"
                  className="w-full border border-gray-300 px-3.5 py-2 rounded-lg text-xs text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] transition"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className="w-full border border-gray-300 px-3.5 py-2 rounded-lg text-xs text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] transition"
                />
              </div>
            </div>
          )}

          {/* Remember Me Checkbox */}
          <div className="pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                name="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded text-[#E8612C] focus:ring-[#E8612C] border-gray-300 accent-[#E8612C] cursor-pointer"
              />
              <span className="text-xs font-medium text-[#475569]">Remember me on this device</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-[#E8612C] text-white py-3 rounded-lg hover:bg-[#D44E1C] transition-all font-semibold shadow-md disabled:opacity-50 mt-2 cursor-pointer ${
              loading ? "cursor-not-allowed opacity-75" : ""
            }`}
          >
            {loading ? "Creating Account..." : "Create Account & Start Free Trial"}
          </button>

          <p className="text-xs text-center text-[#6B7A90] mt-3">
            Already registered?{" "}
            <Link to="/login" className="text-[#E8612C] font-semibold hover:underline">
              Sign In
            </Link>
          </p>

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
              <span className="bg-white px-2.5 text-[#6B7A90] font-medium">Or sign up with</span>
            </div>
          </div>

          {/* Google & Apple Social Sign Up */}
          <SocialAuthButtons role={formData.role || "Customer"} mode="signup" />
        </form>
      </div>
    </div>
  );
}
